import React, { useEffect, useState } from 'react';
import { Modal, Button, Col, Row, Form, Input, Switch, Select, notification } from 'antd';
import { CloseOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import InputMask from 'react-input-mask';
import { cadMotoristaServices } from '../../services/cadMotoristaServices';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../store';
import TextFilter from '../../../../../components/TextFilter';
import TitlePage from '../../../../../components/TitlePage';
import { loadRequest } from '../../../../../store/ducks/motorista/actions';
import { useDispatch } from 'react-redux';
import { formatPhoneNumber } from '../../../../../utils/mask';
import { cpfValidation, phoneValidation } from '../../../../../utils/validator';

export interface Props {
  record: {
    codMotorista: number;
    cpf: string;
    habilitado: string;
    mopp: boolean;
    nome: string;
    telefone: string;
  };
}

const ModalEditaMotorista: React.FC<Props> = ({ record }) => {
  const [form] = Form.useForm();
  const { Option } = Select;
  const dispatch = useDispatch();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const inscricaoAtiva = useSelector((state: ApplicationState) => state.auth.inscricaoAtiva);
  const filter = useSelector((state: ApplicationState) => state.Motorista.filter);
  const pagination = useSelector((state: ApplicationState) => state.Motorista.pagination);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(record);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  const handleCancel = () => {
    form.resetFields();
    setIsModalVisible(false);
  };

  const handleOk = async (e: any) => {
    const isValidCPF = cpfValidation(e.cpf);

    if (!isValidCPF) return;

    const isValidPhone = phoneValidation(e.telefone);

    if (!isValidPhone) {
      return notification.error({
        message: 'Telefone inválido',
        placement: 'bottomRight',
      });
    }

    try {
      const cpfCleaned = e.cpf?.replace(/[^\d]+/g, '');
      const motorista: any = {
        codMotorista: record.codMotorista,
        nome: e.nome,
        cpf: cpfCleaned,
        mopp: e.mopp,
        habilitado: e.habilitado,
        telefone: e.telefone,
      };

      await cadMotoristaServices
        .criaMotorista(motorista, inscricaoAtiva)
        .then((res) => {
          Swal.fire({
            title: 'Motorista cadastrado com sucesso!',
            html: '',
            icon: 'success',
            showConfirmButton: false,
            timer: 1500,
          });
          form.resetFields();
        })
        .then(() => {
          setIsModalVisible(false);
          setTimeout(() => {
            dispatch(loadRequest(inscricaoAtiva, filter, pagination));
          }, 0);
        });
    } catch (error: any) {
      if (error.response.data.Erro) {
        Swal.fire({
          title: 'Opss...',
          html: `${error.response?.data?.Erro}`,
          icon: 'warning',
          showConfirmButton: true,
        });
      } else {
        Swal.fire({
          title: 'Opss...',
          html:
            'Ocorreu um erro ao cadastrar o motorista, por favor atualize a página, se persistir o erro contate o suporte técnico!.',
          icon: 'warning',
          showConfirmButton: true,
        });
      }
    }
  };

  return (
    <>
      <Modal
        centered
        title={<TitlePage>Editar Motorista</TitlePage>}
        visible={isModalVisible}
        onCancel={handleCancel}
        maskClosable={true}
        closable={true}
        destroyOnClose={true}
        closeIcon={<CloseOutlined />}
        keyboard={true}
        width={850}
        footer={[
          <Button
            key="back"
            type="ghost"
            size="middle"
            onClick={handleCancel}
            icon={<CloseOutlined />}
          >
            Cancelar
          </Button>,
          <Button
            type="primary"
            data-cy="btn-editar-motorista"
            onClick={() => {
              form
                .validateFields()
                .then((values) => {
                  handleOk(values);
                })
                .catch((info) => { });
            }}
            icon={<SaveOutlined />}
          >
            Salvar
          </Button>,
        ]}
      >
        <Form form={form}>
          <Row gutter={24}>
            <Col md={12} sm={24} xs={24}>
              <Form.Item
                label={<TextFilter>Nome completo</TextFilter>}
                style={{ display: 'table', width: '100%' }}
                name="nome"
                rules={[
                  { required: true, message: 'Digite o nome completo' },
                  { min: 5, message: 'Digite o nome completo' },
                  { max: 70, message: 'Digite o nome completo' },
                ]}
              >
                <Input data-cy="nome"
                  name="nome"
                  type="text"
                  minLength={5}
                  placeholder="Digite o nome completo"
                  maxLength={70}
                />
              </Form.Item>
            </Col>

            <Col md={12} sm={24} xs={24}>
              <Form.Item
                label={<TextFilter>CPF</TextFilter>}
                style={{ display: 'table', width: '100%' }}
                name="cpf"
                rules={[{ required: true, message: 'Por favor digite o número do CPF!' }]}
              >
                <InputMask data-cy="cpf"
                  className="ant-input"
                  mask="999.999.999-99"
                  maskPlaceholder={null}
                  name="cnpjCpf"
                  placeholder="999.999.999-99"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col md={12} sm={6} xs={6}>
              <Form.Item
                label={<TextFilter>Categoria da CNH</TextFilter>}
                style={{ display: 'table', width: '100%' }}
                name="habilitado"
                rules={[{ required: true, message: 'Por favor selecione uma categoria!' }]}
              >
                <Select data-cy="categoriaCNH" defaultValue="" style={{ width: '100%' }}>
                  <Option value="A">A</Option>
                  <Option value="B">B</Option>
                  <Option value="AB">AB</Option>
                  <Option value="C">C</Option>
                  <Option value="AC">AC</Option>
                  <Option value="D">D</Option>
                  <Option value="AD">AD</Option>
                  <Option value="E">E</Option>
                  <Option value="AE">AE</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col md={12} sm={24} xs={24}>
              <Form.Item
                name="telefone"
                label={<TextFilter>Telefone</TextFilter>}
                style={{ display: 'table', width: '100%' }}
                rules={[
                  {
                    required: true,
                    message: 'Por favor digite o número do telefone!',
                  },
                ]}
              >
                <Input data-cy="telefone"
                  maxLength={15}
                  onChange={(e) => {
                    form.setFieldsValue({ telefone: formatPhoneNumber(e.target.value) });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col md={6} sm={6} xs={6}>
              <Form.Item
                label={<TextFilter>Possui MOPP</TextFilter>}
                style={{ display: 'table', width: '100%' }}
                name="mopp"
                initialValue={record?.mopp}
                valuePropName={record?.mopp ? 'checked' : 'unchecked'}
              >
                <Switch checkedChildren="SIM" unCheckedChildren="NÃO" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Button
        onClick={() => setIsModalVisible(true)}
        className="outlineBtn"
        icon={<EditOutlined />}
      >
        Editar
      </Button>
    </>
  );
};

export default ModalEditaMotorista;
